type NullableNumber = number | null;

type EngagementDayPayload = {
  currentStreak?: unknown;
  previousStreak?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const coerceToNumber = (value: unknown): NullableNumber => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const isNormalizedEngagement = (
  value: unknown
): value is NormalizedParticipantEngagement => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(value, 'days') ||
    Object.prototype.hasOwnProperty.call(value, 'previousDays') ||
    Object.prototype.hasOwnProperty.call(value, 'delta')
  );
};

export type NormalizedParticipantEngagement = {
  days: NullableNumber;
  previousDays: NullableNumber;
  delta: NullableNumber;
};

export const normalizeParticipantEngagement = (
  rawValue: unknown
): NormalizedParticipantEngagement => {
  if (isNormalizedEngagement(rawValue)) {
    return {
      days: coerceToNumber(rawValue.days),
      previousDays: coerceToNumber(rawValue.previousDays),
      delta: coerceToNumber(rawValue.delta),
    };
  }

  const payload = rawValue as EngagementDayPayload;

  const days = coerceToNumber(payload?.currentStreak);
  const previousDays = coerceToNumber(payload?.previousStreak);

  const delta =
    days !== null && previousDays !== null ? days - previousDays : null;

  return {
    days,
    previousDays,
    delta,
  };
};

export const hasEngagementData = (value: NormalizedParticipantEngagement) => {
  return (
    value.days !== null || value.previousDays !== null || value.delta !== null
  );
};
